import React from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  Alert,
  Chip,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { DaySlotSelection } from '../types';

/** 「希望」選択時の色（緑・WCAG 2.1 AA準拠） */
const PREFERRED_COLOR = '#376a53';
/** 「希望」選択時の背景色（薄緑） */
const PREFERRED_BG_COLOR = '#e7f2e9';
/** 「不可」選択時の色（赤・WCAG 2.1 AA準拠） */
const UNAVAILABLE_COLOR = '#9f545c';
/** 「不可」選択時の背景色（薄赤） */
const UNAVAILABLE_BG_COLOR = '#fde9e9';

/**
 * タイムスロット選択UIのProps
 */
interface TimeSlotPickerProps {
  /** スロット一覧（募集枠または自分のシフト行から生成） */
  slots: DaySlotSelection[];
  /**
   * スロット選択変更ハンドラー
   * @param key - 変更されたスロットのキー（`${start}-${end}`）
   * @param selected - 選択されたか（false = 解除）
   */
  onChange: (key: string, selected: boolean) => void;
  /** 全体無効化フラグ（過去日付・編集不可フェーズ） */
  disabled?: boolean;
  /**
   * ボタンバリアント
   * @description 'preferred'（通常募集: 勤務したい枠に「希望」を付ける）/
   * 'unavailable'（固定シフト募集中: 勤務できない枠に「不可」を付ける。
   * 不可 = selected を false にする操作で、不可にした枠は保存時に削除される）
   */
  variant?: 'preferred' | 'unavailable';
}

/**
 * タイムスロット選択UIコンポーネント
 * @description 募集している枠のみを表示する（固定の時間帯一覧は生成しない）
 */
export const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  slots,
  onChange,
  disabled = false,
  variant = 'preferred',
}) => {
  const isUnavailableVariant = variant === 'unavailable';
  const selectedCount = slots.filter(slot => !slot.locked && slot.selected).length;
  const unavailableCount = slots.filter(slot => !slot.locked && !slot.selected).length;

  const handleToggle = (slot: DaySlotSelection) => {
    if (!disabled && !slot.locked) {
      onChange(slot.key, !slot.selected);
    }
  };

  if (slots.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {isUnavailableVariant
          ? 'この日に対象の固定シフトはありません。'
          : 'この日に募集している枠はありません。'}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        タイムスロット選択
      </Typography>
      {!disabled && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isUnavailableVariant
            ? '勤務できない時間帯の「不可」を選択してください（不可にした枠は保存時に削除されます）'
            : '希望する時間帯の「希望」を選択してください'}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: 400,
          overflowY: 'auto',
        }}
      >
        {slots.map(slot => {
          const rowColor = slot.locked
            ? '#eeece9'
            : slot.selected
              ? PREFERRED_BG_COLOR
              : isUnavailableVariant
                ? UNAVAILABLE_BG_COLOR
                : '#f8f7f5';

          return (
            <Box
              key={slot.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                p: 1,
                borderRadius: 1,
                backgroundColor: rowColor,
                transition: 'background-color 0.2s',
                opacity: slot.locked ? 0.75 : 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ minWidth: 100, fontWeight: 'medium' }}
              >
                {slot.startTime} - {slot.endTime}
              </Typography>


              {slot.locked ? (
                <Chip
                  icon={slot.source === 'rule' ? <LockIcon fontSize="small" /> : undefined}
                  label={
                    slot.selected
                      ? slot.source === 'rule'
                        ? '確定済み'
                        : '選択済み'
                      : '未選択'
                  }
                  size="small"
                  color={slot.selected ? 'default' : 'default'}
                  variant="outlined"
                />
              ) : (
                <ToggleButton
                  value={isUnavailableVariant ? 'unavailable' : 'preferred'}
                  selected={isUnavailableVariant ? !slot.selected : slot.selected}
                  onChange={() => handleToggle(slot)}
                  disabled={disabled}
                  size="small"
                  aria-label={
                    isUnavailableVariant
                      ? `${slot.startTime}-${slot.endTime}を不可にする`
                      : `${slot.startTime}-${slot.endTime}を希望する`
                  }
                  sx={{
                    px: 2.5,
                    '&.Mui-selected': {
                      backgroundColor: isUnavailableVariant
                        ? UNAVAILABLE_COLOR
                        : PREFERRED_COLOR,
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: isUnavailableVariant
                          ? UNAVAILABLE_COLOR
                          : PREFERRED_COLOR,
                        opacity: 0.9,
                      },
                    },
                  }}
                >
                  {isUnavailableVariant ? '不可' : '希望'}
                </ToggleButton>
              )}
            </Box>
          );
        })}
      </Box>

      {!disabled && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, textAlign: 'right' }}
        >
          {isUnavailableVariant
            ? `不可: ${unavailableCount}スロット`
            : `選択中: ${selectedCount}スロット`}
        </Typography>
      )}
    </Box>
  );
};

export default TimeSlotPicker;
